import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { PointerProps } from "react-gauge-component";

export const DEFAULT_DARK_COLOR = "inherit";
export const DEFAULT_RED_COLOR = "#D95648";
export const DOUBLE_CLICK_THRESHOLD = 300;

export const pointerConfig: PointerProps = {
  type: "arrow",
  animate: false,
  color: DEFAULT_DARK_COLOR,
  length: 1,
  width: 10,
};

export interface DialIndicatorProps {
  /** Dataset identifier from which the current value is retrieved */
  valueDataset: string;

  /** Minimum value of the dial; can be a number or a dataset ID if minValueType is "dataset" */
  minValue: string | number;

  /** Type of the minValue (constant or dataset) */
  minValueType: ValueType;

  /** Maximum value of the dial; can be a number or a dataset ID if maxValueType is "dataset" */
  maxValue: string | number;

  /** Type of the maxValue (constant or dataset) */
  maxValueType: ValueType;

  /** Array of value zones with specific colors for display */
  zones: Zone[];

  /** Whether to hide the numeric value text inside the dial */
  hideTextValue?: boolean;

  /** Whether to hide the tick marks on the dial */
  hideTicks?: boolean;

  /** Array of custom tick mark values to be shown on the dial */
  ticks: Tick[];

  /** Color of the last zone (optional, overrides the last zone color if set) */
  lastZone: LastZone;

  /** First value override (optional) */
  minValueOverride?: string;

  /** Last value override (optional) */
  maxValueOverride?: string;

  /** How often the dial should refresh its data, in seconds (optional) */
  updateFrequency?: number;

  /** Font size for tick labels (optional) */
  ticksFontSize?: number;

  /** Whether to hide the numeric value text inside the dial */
  hideValue?: boolean;
}

/** Props passed to the dial indicator editor in the dashboard editor context */
export interface DialIndicatorEditorProps extends DashboardEditorProps<DialIndicatorProps> {}

/** Props for the min or max value input editor of the dial indicator */
export interface DialIndicatorMinMaxValueEditorProps
  extends DashboardEditorProps<DialIndicatorProps> {
  /** Whether the editor is currently editing the minimum value (true) or maximum value (false) */
  isMinValue: boolean;
}

/** Defines how a min or max value is interpreted: either as a constant number or from another dataset */
export enum ValueType {
  /** Use a fixed number for min or max value */
  CONSTANT = "constant",

  /** Use a dataset value for min or max value */
  DATASET = "dataset",

  /** Use a string value for min or max value */
  STRING = "string",
}

/** A colored zone shown on the dial, up to the last zone */
export interface LastZone {
  /** Display color for this zone */
  color: string;

  /** Whether to highlight the current value in this zone */
  highlightValue?: boolean;
}

/** A colored zone shown on the dial, up to a specified maxValue */
export interface Zone {
  [x: string]: any;
  /** Type of the zone max value (constant or dataset) */
  valueType: ValueType;

  /** Maximum value of the zone; can be a number or a dataset ID if valueType is "dataset" */
  maxValue: number | string;

  /** String or numeric representation of the zone max value (optional) */
  overriddenValue?: string | number;

  /** Display color for this zone */
  color: string;

  /** Whether to highlight the current value in this zone */
  highlightValue?: boolean;
}

/** A custom tick mark shown at a specific value on the dial */
export interface Tick {
  /** Value at which the tick should be displayed */
  value: number;

  /** String or numeric representation of the tick value (optional) */
  overriddenValue?: string | number;
}
